// Estado unico dos filtros do funil e as conversoes entre URL, preferencias salvas e parametros da API. Os nomes
// da URL e da API sao os mesmos (specs/005-funil-filtros-avancados/contracts/frontend.md); valores padrao sao
// omitidos nos dois.
import {
  DATE_RANGE_OPERATORS,
  isValidDateRangeValues,
} from 'dashboard/components-next/filter/helper/dateRangeOperators';

export const PERIODS = ['last_month', 'current_month', 'next_month'];
export const DATE_FIELDS = ['created_at', 'expected_close_date'];
export const DUE_STATUSES = [
  'overdue',
  'due_today',
  'next_7_days',
  'no_due_date',
];
// Valor so de UI: o intervalo vai para a API e a URL como due_from/due_to.
export const DUE_RANGE = 'range';

// Condicoes sobre atributos personalizados de cartao: mesmo quadro de FazerAi::Kanban::TaskFilter
// (contracts/attribute-operators.md). Os quatro operadores de data por periodo vem da Onda 0.
export const MAX_ATTRIBUTE_FILTERS = 10;
export const MAX_LIST_VALUES = 50;
export const NO_VALUE_OPERATORS = ['is_present', 'is_not_present'];
const NUMBER_OPERATORS = [
  'is_greater_than',
  'is_less_than',
  'between',
  ...NO_VALUE_OPERATORS,
];
export const OPERATORS_BY_TYPE = {
  date: [
    ...DATE_RANGE_OPERATORS,
    'is_greater_than',
    'is_less_than',
    ...NO_VALUE_OPERATORS,
  ],
  list: ['equal_to', ...NO_VALUE_OPERATORS],
  checkbox: ['equal_to'],
  text: ['contains', 'equal_to', ...NO_VALUE_OPERATORS],
  number: NUMBER_OPERATORS,
  currency: NUMBER_OPERATORS,
  percent: NUMBER_OPERATORS,
  link: NO_VALUE_OPERATORS,
};

export const DEFAULT_FILTER_STATE = Object.freeze({
  agentId: 'all',
  inboxId: 'all',
  dateField: 'created_at',
  period: 'all',
  label: 'all',
  source: 'all',
  productId: 'all',
  showCompleted: false,
  showCancelled: false,
  dueStatus: 'all',
  dueFrom: null,
  dueTo: null,
  attributeFilters: Object.freeze([]),
  // Busca de texto do quadro: vale so na sessao (nao vai para as preferencias nem para a URL).
  search: '',
});

// Filtros simples: [chave do estado, parametro da URL/API/preferencias]; 'all' e o padrao de todos.
const SIMPLE_FIELDS = [
  ['agentId', 'agent_id'],
  ['inboxId', 'inbox_id'],
  ['label', 'label'],
  ['source', 'source'],
  ['productId', 'product_id'],
];
const STATUS_FIELDS = [
  ['showCompleted', 'show_completed'],
  ['showCancelled', 'show_cancelled'],
];
// Ids da URL so com digitos: a API recusa outra coisa (422).
const ID_FIELDS = ['agentId', 'inboxId', 'productId'];
const ID = /^\d+$/;
const QUERY_KEYS = [
  ...SIMPLE_FIELDS.map(([, param]) => param),
  ...STATUS_FIELDS.map(([, param]) => param),
  'period',
  'date_field',
  'due_status',
  'due_from',
  'due_to',
  'attribute_filters',
];

export const createFilterState = (overrides = {}) => ({
  ...DEFAULT_FILTER_STATE,
  ...overrides,
  attributeFilters: [...(overrides.attributeFilters || [])],
});

const filled = value =>
  value !== undefined && value !== null && String(value) !== '';

const isDay = value =>
  typeof value === 'string' &&
  isValidDateRangeValues('between', [value, value]);

const NUMBER = /^-?\d+(\.\d+)?$/;

const hasValues = (values, size, isValid) =>
  values.length === size && values.every(isValid);

const isValidValues = (type, operator, values) => {
  if (NO_VALUE_OPERATORS.includes(operator)) return values.length === 0;
  if (type === 'date') {
    if (DATE_RANGE_OPERATORS.includes(operator)) {
      const size = operator === 'between' ? 2 : 1;
      return values.length === size && isValidDateRangeValues(operator, values);
    }
    return hasValues(values, 1, isDay);
  }
  if (type === 'list') {
    return (
      values.length >= 1 &&
      values.length <= MAX_LIST_VALUES &&
      values.every(value => value.trim() !== '')
    );
  }
  if (type === 'checkbox') {
    return hasValues(values, 1, value => ['true', 'false'].includes(value));
  }
  if (type === 'text')
    return hasValues(values, 1, value => value.trim() !== '');
  const size = operator === 'between' ? 2 : 1;
  if (!hasValues(values, size, value => NUMBER.test(value))) return false;
  return operator !== 'between' || Number(values[0]) <= Number(values[1]);
};

// Valida uma condicao contra as definicoes task_attribute da conta (mesma regra do servidor).
export const isValidCondition = (condition, definitions) => {
  if (!condition || typeof condition !== 'object') return false;
  const { attribute_key: key, filter_operator: operator, values } = condition;
  const definition = (definitions || []).find(
    item => item.attribute_key === key
  );
  if (!definition) return false;
  const type = definition.attribute_display_type;
  if (!OPERATORS_BY_TYPE[type]?.includes(operator)) return false;
  if (
    !Array.isArray(values) ||
    !values.every(value => typeof value === 'string')
  ) {
    return false;
  }
  return isValidValues(type, operator, values);
};

// Lista de condicoes vinda da URL (JSON) ou das preferencias (array). Sem definicoes (uso do store) nada e
// validado: a pagina ja gravou o estado limpo antes de buscar.
const readAttributeFilters = (raw, { strict, definitions }) => {
  let conditions = raw;
  if (strict) {
    try {
      conditions = JSON.parse(raw);
    } catch {
      return { conditions: [], dropped: 1 };
    }
  }
  if (!Array.isArray(conditions)) return { conditions: [], dropped: 1 };
  if (!definitions) return { conditions, dropped: 0 };

  const kept = [];
  conditions.forEach(condition => {
    const isDuplicate = kept.some(
      item => item.attribute_key === condition?.attribute_key
    );
    if (
      kept.length < MAX_ATTRIBUTE_FILTERS &&
      !isDuplicate &&
      isValidCondition(condition, definitions)
    ) {
      kept.push({
        attribute_key: condition.attribute_key,
        filter_operator: condition.filter_operator,
        values: [...condition.values],
      });
    }
  });
  return { conditions: kept, dropped: conditions.length - kept.length };
};

// Le um estado a partir de um objeto com os nomes de parametro (preferencias ou query), descartando o que nao vale.
const readState = (source, { strict, definitions }) => {
  const state = createFilterState();
  let dropped = 0;
  const reject = () => {
    dropped += 1;
  };

  SIMPLE_FIELDS.forEach(([key, param]) => {
    const value = source[param];
    if (!filled(value)) return;
    const invalidId = ID_FIELDS.includes(key) && !ID.test(String(value));
    if (strict && (typeof value !== 'string' || invalidId)) {
      reject();
      return;
    }
    state[key] = String(value);
  });

  if (filled(source.period)) {
    if (source.period === 'all' || PERIODS.includes(source.period)) {
      state.period = source.period;
    } else reject();
  }
  if (filled(source.date_field)) {
    if (DATE_FIELDS.includes(source.date_field)) {
      state.dateField = source.date_field;
    } else reject();
  }
  STATUS_FIELDS.forEach(([key, param]) => {
    state[key] = source[param] === true || source[param] === 'true';
  });

  let range = ['due_from', 'due_to'].map(param => {
    if (!filled(source[param])) return null;
    if (isDay(source[param])) return source[param];
    reject();
    return null;
  });
  if (range[0] && range[1] && range[0] > range[1]) {
    reject();
    range = [null, null];
  }
  const status = source.due_status;
  // Na URL o intervalo e so due_from/due_to; nas preferencias a UI guarda 'range'.
  const statuses = strict ? DUE_STATUSES : ['all', DUE_RANGE, ...DUE_STATUSES];
  if (filled(status) && !statuses.includes(status)) reject();
  else if (DUE_STATUSES.includes(status)) {
    state.dueStatus = status;
    if (range.some(Boolean)) reject();
  } else if (range.some(Boolean) || status === DUE_RANGE) {
    state.dueStatus = DUE_RANGE;
    [state.dueFrom, state.dueTo] = range;
  }

  if (filled(source.attribute_filters)) {
    const attributes = readAttributeFilters(source.attribute_filters, {
      strict,
      definitions,
    });
    state.attributeFilters = attributes.conditions;
    dropped += attributes.dropped;
  }

  return { state, dropped };
};

export const stateFromPreferences = (saved, definitions = null) =>
  readState(saved || {}, { strict: false, definitions });

export const stateFromQuery = (query, definitions) =>
  readState(query || {}, { strict: true, definitions });

export const hasFilterQuery = query =>
  QUERY_KEYS.some(key => Object.hasOwn(query || {}, key));

export const stateToPreferences = state => {
  const current = createFilterState(state);
  return {
    agent_id: current.agentId,
    inbox_id: current.inboxId,
    period: current.period,
    date_field: current.dateField,
    show_completed: current.showCompleted,
    show_cancelled: current.showCancelled,
    label: current.label,
    source: current.source,
    product_id: current.productId,
    due_status: current.dueStatus,
    due_from: current.dueStatus === DUE_RANGE ? current.dueFrom : null,
    due_to: current.dueStatus === DUE_RANGE ? current.dueTo : null,
    attribute_filters: current.attributeFilters,
  };
};

export const stateToApiParams = state => {
  const current = createFilterState(state);
  const params = {};
  SIMPLE_FIELDS.forEach(([key, param]) => {
    if (current[key] !== 'all') params[param] = current[key];
  });
  if (PERIODS.includes(current.period)) {
    params.period = current.period;
    params.date_field = current.dateField;
  }
  if (DUE_STATUSES.includes(current.dueStatus)) {
    params.due_status = current.dueStatus;
  } else if (current.dueStatus === DUE_RANGE) {
    if (current.dueFrom) params.due_from = current.dueFrom;
    if (current.dueTo) params.due_to = current.dueTo;
  }
  if (current.search.trim()) params.q = current.search.trim();
  if (current.attributeFilters.length) {
    params.attribute_filters = JSON.stringify(current.attributeFilters);
  }
  return params;
};

export const stateToQuery = state => {
  const current = createFilterState(state);
  const query = stateToApiParams(current);
  delete query.q;
  if (current.dateField !== DEFAULT_FILTER_STATE.dateField) {
    query.date_field = current.dateField;
  } else delete query.date_field;
  STATUS_FIELDS.forEach(([key, param]) => {
    if (current[key]) query[param] = 'true';
  });
  return query;
};

export const activeFilterCount = state =>
  createFilterState(state).attributeFilters.length;
